import React, { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import { Trophy, Flame, Award, Sparkles, Target, TrendingUp, Lock, ChevronRight, Calendar as CalendarIcon, Star } from 'lucide-react';
import { SeasonStoryModal } from '@/components/SeasonStory';
import { BadgeShareButton } from '@/components/BadgeShareCard';
import { BadgeCelebration } from '@/components/BadgeCelebration';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function Progress({ user }) {
  const [data, setData] = useState(null);
  const [memory, setMemory] = useState([]);
  const [quests, setQuests] = useState(null);
  const [gardenScore, setGardenScore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');
  const [showSeason, setShowSeason] = useState(false);
  const [celebrate, setCelebrate] = useState(null);

  const isPremium = user?.subscription_active && (user?.subscription_tier === 'premium' || user?.subscription_tier === 'pro');

  useEffect(() => {
    const fetch = async () => {
      try {
        const [p, m, q, s] = await Promise.all([
          axios.get(`${API}/progress`, { withCredentials: true }),
          axios.get(`${API}/memory-timeline`, { withCredentials: true }),
          axios.get(`${API}/quests/active`, { withCredentials: true }),
          axios.get(`${API}/garden-score`, { withCredentials: true })
        ]);
        setData(p.data);
        setMemory(m.data);
        setQuests(q.data);
        setGardenScore(s.data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetch();
  }, []);

  const completeQuest = async (id) => {
    try {
      await axios.post(`${API}/quests/${id}/complete`, {}, { withCredentials: true });
      const [p, q] = await Promise.all([
        axios.get(`${API}/progress`, { withCredentials: true }),
        axios.get(`${API}/quests/active`, { withCredentials: true })
      ]);
      setData(p.data);
      setQuests(q.data);
    } catch (e) { console.error(e); }
  };

  if (loading) return <Layout user={user}><div className="flex items-center justify-center h-96"><div className="w-16 h-16 border-4 border-[#8EAA97] border-t-[#1A4331] rounded-full animate-spin"></div></div></Layout>;

  const level = data?.level || {};
  const xpProgressPct = level.next_level_xp > level.current_level_xp
    ? Math.round(((level.current_xp - level.current_level_xp) / (level.next_level_xp - level.current_level_xp)) * 100)
    : 100;

  const unlockedAchs = data?.achievements?.filter(a => a.unlocked) || [];
  const lockedAchs = data?.achievements?.filter(a => !a.unlocked) || [];

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'skills', label: 'Skill Tree' },
    { id: 'achievements', label: 'Achievements' },
    { id: 'memory', label: 'Garden Memory' },
    { id: 'bests', label: 'Personal Bests' },
  ];

  return (
    <Layout user={user}>
      <div className="space-y-6">
        {/* Hero: Grower Level */}
        <div className="bg-gradient-to-br from-[#1A4331] via-[#255C44] to-[#1A4331] rounded-3xl p-8 text-[#FAF6EC] relative overflow-hidden" data-testid="grower-level-hero">
          <div className="absolute top-0 right-0 w-64 h-64 bg-[#C9A85B]/10 rounded-full blur-3xl"></div>
          <div className="relative flex items-start justify-between gap-6 flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <div className="flex items-center gap-2 mb-2">
                <Trophy className="w-5 h-5 text-[#C9A85B]" />
                <span className="text-xs font-bold tracking-[0.2em] uppercase opacity-80">Level {level.level}</span>
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {level.title}
              </h1>
              <p className="text-sm opacity-90 mt-3 leading-relaxed max-w-md">
                Every harvest, every check-in, every lesson — Homegrown tracks the gardener you&apos;re becoming.
              </p>
            </div>
            <div className="text-right">
              <p className="text-5xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>{data.total_xp}</p>
              <p className="text-xs tracking-wider uppercase opacity-70 mt-1">total XP</p>
            </div>
          </div>
          <div className="mt-6 relative">
            <div className="flex items-center justify-between text-xs opacity-80 mb-2">
              <span>{level.current_xp - level.current_level_xp} XP this level</span>
              {!level.is_max && <span>{level.next_level_xp - level.current_xp} XP to next</span>}
              {level.is_max && <span>MAX LEVEL</span>}
            </div>
            <div className="h-2 bg-white/20 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-[#C9A85B] to-[#B4D330] transition-all duration-1000" style={{ width: `${level.is_max ? 100 : xpProgressPct}%` }}></div>
            </div>
          </div>
        </div>

        {/* Season Wrap Story button */}
        <button onClick={() => setShowSeason(true)} data-testid="season-story-button"
          className="w-full bg-gradient-to-br from-[#C66B4A]/15 to-[#FAF6EC] rounded-3xl p-5 border border-[#C66B4A]/30 flex items-center gap-4 text-left hover:scale-[1.01] transition-transform">
          <div className="w-12 h-12 bg-[#C66B4A]/15 rounded-full flex items-center justify-center flex-shrink-0">
            <Star className="w-6 h-6 text-[#C66B4A]" />
          </div>
          <div className="flex-1">
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#C66B4A]">Season so far</p>
            <p className="text-lg font-semibold text-[#1A4331] mt-0.5" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Your season wrap — value grown, best crops & skills gained
            </p>
          </div>
          <ChevronRight className="w-5 h-5 text-[#7A6B57]" />
        </button>

        {/* Streak Strip */}
        <div className="grid grid-cols-3 gap-3">
          {[
            { key: 'garden_check', label: 'Garden check-in', icon: Flame, color: 'text-[#C66B4A]' },
            { key: 'weekly_plan', label: 'Weekly plan', icon: CalendarIcon, color: 'text-[#C9A85B]' },
            { key: 'harvest_log', label: 'Harvest log', icon: TrendingUp, color: 'text-[#6B9F4A]' }
          ].map(s => {
            const Icon = s.icon;
            const val = data.streaks?.[s.key] || 0;
            return (
              <div key={s.key} className="bg-[#FAF6EC] rounded-2xl p-4 border border-[#1A4331]/8" data-testid={`streak-${s.key}`}>
                <Icon className={`w-5 h-5 ${s.color} mb-2`} />
                <p className="text-2xl font-bold text-[#1A4331]" style={{ fontFamily: 'Outfit, sans-serif' }}>{val}</p>
                <p className="text-xs text-[#7A6B57]">{s.label} {val === 1 ? 'day' : 'days'}</p>
              </div>
            );
          })}
        </div>

        {/* Garden Score */}
        {gardenScore && (
          <div className="bg-[#FAF6EC] rounded-3xl p-6 border border-[#1A4331]/10" data-testid="garden-score">
            <div className="flex items-start justify-between mb-4 flex-wrap gap-4">
              <div>
                <p className="text-xs tracking-wider uppercase text-[#7A6B57] font-semibold">Garden Status</p>
                <h2 className="text-3xl font-bold text-[#1A4331] mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{gardenScore.label}</h2>
              </div>
              <div className="text-right">
                <p className="text-5xl font-bold text-[#1A4331]" style={{ fontFamily: 'Outfit, sans-serif' }}>{gardenScore.score}<span className="text-2xl text-[#7A6B57]">/100</span></p>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4 mt-4">
              <div>
                <p className="text-xs font-semibold text-[#6B9F4A] uppercase tracking-wider mb-2">Strong</p>
                {gardenScore.strong?.length > 0 ? (
                  <ul className="space-y-1">{gardenScore.strong.map(s => <li key={s} className="text-sm text-[#2A3528]">• {s}</li>)}</ul>
                ) : <p className="text-sm text-[#7A6B57] italic">Keep going — strengths grow with consistency.</p>}
              </div>
              <div>
                <p className="text-xs font-semibold text-[#C66B4A] uppercase tracking-wider mb-2">Needs attention</p>
                {gardenScore.attention?.length > 0 ? (
                  <ul className="space-y-1">{gardenScore.attention.map(s => <li key={s} className="text-sm text-[#2A3528]">• {s}</li>)}</ul>
                ) : <p className="text-sm text-[#7A6B57] italic">Nothing critical right now.</p>}
              </div>
            </div>
          </div>
        )}

        {/* Active Quests */}
        {quests?.weekly && (
          <div className="bg-gradient-to-br from-[#C9A85B]/10 to-[#FAF6EC] rounded-3xl p-6 border border-[#C9A85B]/30" data-testid="weekly-quest">
            <div className="flex items-start gap-4 flex-wrap">
              <div className="w-12 h-12 bg-[#C9A85B]/20 rounded-full flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-6 h-6 text-[#C9A85B]" />
              </div>
              <div className="flex-1 min-w-[200px]">
                <p className="text-xs font-bold tracking-wider uppercase text-[#C9A85B]">Weekly Quest · +{quests.weekly.xp_reward} XP</p>
                <h3 className="text-xl font-semibold text-[#1A4331] mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{quests.weekly.title}</h3>
                <p className="text-xs text-[#7A6B57] mt-2 capitalize">Skill: {quests.weekly.skill?.replace(/_/g, ' ')}</p>
              </div>
              {quests.weekly.status !== 'completed' && (
                <button onClick={() => completeQuest(quests.weekly.id)} data-testid="complete-quest-button" className="bg-[#1A4331] hover:bg-[#0F2D1F] text-[#FAF6EC] font-semibold py-2 px-5 rounded-full text-sm transition-colors">
                  Mark complete
                </button>
              )}
              {quests.weekly.status === 'completed' && (
                <span className="bg-[#6B9F4A]/20 text-[#6B9F4A] font-semibold py-2 px-5 rounded-full text-sm">Completed</span>
              )}
            </div>
          </div>
        )}
        {quests?.seasonal && (
          <div className="bg-[#FAF6EC] rounded-3xl p-5 border border-[#1A4331]/10" data-testid="seasonal-quest">
            <div className="flex items-center gap-3">
              <Target className="w-5 h-5 text-[#6B9F4A]" />
              <div className="flex-1">
                <p className="text-xs font-bold tracking-wider uppercase text-[#6B9F4A]">Seasonal Quest · +{quests.seasonal.xp} XP</p>
                <p className="text-sm text-[#1A4331] font-medium mt-1">{quests.seasonal.title}</p>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 border-b border-[#1A4331]/10 overflow-x-auto">
          {tabs.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
              className={`px-4 py-3 text-sm font-semibold whitespace-nowrap transition-colors border-b-2 ${tab === t.id ? 'border-[#1A4331] text-[#1A4331]' : 'border-transparent text-[#7A6B57] hover:text-[#1A4331]'}`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {tab === 'overview' && (
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-[#FAF6EC] rounded-3xl p-6 border border-[#1A4331]/10">
              <h3 className="text-lg font-semibold text-[#1A4331] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Recent achievements</h3>
              {unlockedAchs.length === 0 ? (
                <p className="text-sm text-[#7A6B57]">No achievements yet — log a harvest or sow a crop to begin.</p>
              ) : (
                <div className="space-y-2">
                  {unlockedAchs.slice(0, 4).map(a => (
                    <div key={a.achievement_id} className="flex items-center gap-3 p-2">
                      <span className="text-2xl">{a.icon}</span>
                      <div>
                        <p className="text-sm font-semibold text-[#1A4331]">{a.title}</p>
                        <p className="text-xs text-[#7A6B57]">+{a.xp_reward} XP</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="bg-[#FAF6EC] rounded-3xl p-6 border border-[#1A4331]/10">
              <h3 className="text-lg font-semibold text-[#1A4331] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Top skills</h3>
              {data.skills.sort((a, b) => b.xp - a.xp).slice(0, 4).map(s => (
                <div key={s.key} className="mb-3">
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-[#1A4331] font-medium">{s.label}</span>
                    <span className="text-[#7A6B57]">Lv {s.level}</span>
                  </div>
                  <div className="h-1.5 bg-[#E5EBDD] rounded-full overflow-hidden">
                    <div className="h-full bg-[#6B9F4A]" style={{ width: `${Math.min(100, (s.xp % 100))}%` }}></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Skill Tree Tab — premium */}
        {tab === 'skills' && (
          <div>
            {!isPremium && (
              <div className="bg-[#C9A85B]/10 border border-[#C9A85B]/30 rounded-2xl p-4 mb-4 flex items-center gap-3" data-testid="skill-tree-premium-gate">
                <Lock className="w-5 h-5 text-[#C9A85B]" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-[#1A4331]">Full Skill Tree is a Premium feature</p>
                  <p className="text-xs text-[#7A6B57]">Free tier shows your top 4 skills. Upgrade for full tree + reasons.</p>
                </div>
                <a href="/subscription" className="text-sm font-semibold text-[#C9A85B] whitespace-nowrap">Upgrade →</a>
              </div>
            )}
            <div className="grid md:grid-cols-2 gap-3">
              {(isPremium ? data.skills : data.skills.slice(0, 4)).map(s => (
                <div key={s.key} className="bg-[#FAF6EC] rounded-2xl p-5 border border-[#1A4331]/10" data-testid={`skill-${s.key}`}>
                  <div className="flex justify-between items-start mb-2">
                    <h4 className="font-semibold text-[#1A4331]">{s.label}</h4>
                    <span className="bg-[#1A4331] text-[#FAF6EC] text-xs font-bold px-2 py-1 rounded-full">Lv {s.level}</span>
                  </div>
                  <div className="h-2 bg-[#E5EBDD] rounded-full overflow-hidden mb-2">
                    <div className="h-full bg-gradient-to-r from-[#6B9F4A] to-[#1A4331]" style={{ width: `${Math.min(100, s.xp % 100)}%` }}></div>
                  </div>
                  <p className="text-xs text-[#7A6B57]">{s.xp} XP · {100 - (s.xp % 100)} to Lv {s.level + 1}</p>
                  {isPremium && s.last_reason && (
                    <p className="text-xs text-[#1A4331] mt-2 italic">{s.last_reason}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Achievements Tab */}
        {tab === 'achievements' && (
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
            {data.achievements.map(a => {
              const locked = !a.unlocked;
              const premiumLocked = locked && a.tier === 'premium' && !isPremium;
              return (
                <div key={a.achievement_id} data-testid={`ach-${a.achievement_id}`}
                  onClick={a.unlocked ? () => setCelebrate(a) : undefined}
                  className={`rounded-2xl p-5 border transition-transform ${a.unlocked ? 'bg-gradient-to-br from-[#C9A85B]/10 to-[#FAF6EC] border-[#C9A85B]/30 cursor-pointer hg-press hover:scale-[1.02]' : 'bg-[#FAF6EC]/50 border-[#1A4331]/8'}`}>
                  <div className="flex items-start gap-3">
                    <div className={`text-3xl ${locked ? 'grayscale opacity-40' : ''}`}>{a.icon}</div>
                    <div className="flex-1">
                      <div className="flex items-center gap-1 flex-wrap">
                        <h4 className="font-semibold text-[#1A4331]">{a.title}</h4>
                        {premiumLocked && <Lock className="w-3 h-3 text-[#C9A85B]" />}
                      </div>
                      <p className="text-xs text-[#7A6B57] mt-1">{a.description}</p>
                      {a.unlocked ? (
                        <>
                          <p className="text-xs text-[#6B9F4A] font-semibold mt-2">+{a.xp_reward} XP unlocked · tap to celebrate</p>
                          <span onClick={(e) => e.stopPropagation()}>
                            <BadgeShareButton badge={a} userName={user?.name?.split(' ')[0]} />
                          </span>
                        </>
                      ) : (
                        <p className="text-xs text-[#7A6B57] mt-2">+{a.xp_reward} XP</p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Garden Memory Timeline — premium */}
        {tab === 'memory' && (
          <div>
            {!isPremium && (
              <div className="bg-[#C9A85B]/10 border border-[#C9A85B]/30 rounded-2xl p-4 mb-4 flex items-center gap-3" data-testid="memory-premium-gate">
                <Lock className="w-5 h-5 text-[#C9A85B]" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-[#1A4331]">Garden Memory Timeline is a Premium feature</p>
                  <p className="text-xs text-[#7A6B57]">Free tier shows your last 5 events. Upgrade for the full story.</p>
                </div>
                <a href="/subscription" className="text-sm font-semibold text-[#C9A85B] whitespace-nowrap">Upgrade →</a>
              </div>
            )}
            {memory.length === 0 ? (
              <div className="bg-[#FAF6EC] rounded-3xl p-12 text-center border border-[#1A4331]/8">
                <CalendarIcon className="w-12 h-12 text-[#8EAA97] mx-auto mb-4" />
                <h3 className="font-semibold text-[#1A4331]">Your story starts here</h3>
                <p className="text-sm text-[#7A6B57] mt-2">Sow a crop, log a harvest, scan a plant — events appear on your timeline.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {(isPremium ? memory : memory.slice(0, 5)).map(e => (
                  <div key={e.id} className="bg-[#FAF6EC] rounded-2xl p-4 border-l-4 border-[#6B9F4A] border border-[#1A4331]/8" data-testid={`memory-${e.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="text-xs text-[#7A6B57] uppercase tracking-wider font-semibold">{e.event_type}</p>
                        <h4 className="font-semibold text-[#1A4331] mt-1">{e.title}</h4>
                        {e.description && <p className="text-sm text-[#2A3528] mt-1">{e.description}</p>}
                        {e.value_low && e.value_high && (
                          <p className="text-xs text-[#C9A85B] mt-2">Estimated value: ${e.value_low}–${e.value_high}</p>
                        )}
                      </div>
                      <p className="text-xs text-[#7A6B57] whitespace-nowrap">{new Date(e.event_date).toLocaleDateString()}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Personal Bests — premium */}
        {tab === 'bests' && (
          <div>
            {!isPremium && (
              <div className="bg-[#C9A85B]/10 border border-[#C9A85B]/30 rounded-2xl p-4 mb-4 flex items-center gap-3" data-testid="bests-premium-gate">
                <Lock className="w-5 h-5 text-[#C9A85B]" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-[#1A4331]">Personal Bests are a Premium feature</p>
                  <p className="text-xs text-[#7A6B57]">Upgrade to track records over seasons.</p>
                </div>
                <a href="/subscription" className="text-sm font-semibold text-[#C9A85B] whitespace-nowrap">Upgrade →</a>
              </div>
            )}
            {data.personal_bests?.length === 0 ? (
              <div className="bg-[#FAF6EC] rounded-3xl p-12 text-center border border-[#1A4331]/8">
                <Award className="w-12 h-12 text-[#8EAA97] mx-auto mb-4" />
                <h3 className="font-semibold text-[#1A4331]">No personal bests yet</h3>
                <p className="text-sm text-[#7A6B57] mt-2">Log a harvest to set your first record.</p>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-3">
                {(isPremium ? data.personal_bests : []).map(b => (
                  <div key={b.id} className="bg-[#FAF6EC] rounded-2xl p-5 border border-[#C9A85B]/30" data-testid={`best-${b.id}`}>
                    <Award className="w-6 h-6 text-[#C9A85B] mb-2" />
                    <h4 className="font-semibold text-[#1A4331]">{b.title}</h4>
                    <p className="text-2xl font-bold text-[#1A4331] mt-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{b.value} {b.unit}</p>
                    <p className="text-xs text-[#7A6B57] mt-1">{new Date(b.achieved_at).toLocaleDateString()}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      {showSeason && <SeasonStoryModal onClose={() => setShowSeason(false)} />}
      <BadgeCelebration badge={celebrate} userName={user?.name?.split(' ')[0]} onClose={() => setCelebrate(null)} />
    </Layout>
  );
}

export default Progress;
